// Instagram (and Facebook/Threads) render links inside their own in-app
// webview, which cannot hand off to the App Store app. A plain
// https://apps.apple.com link tapped in there just stalls and looks broken.
//
// The fix is to escape the webview first. We try the cheapest escape hatches
// in order and fall back to a page with manual instructions if none of them
// take. Nothing here is documented by Meta, so every step is best-effort and
// must fail silently.

export const APP_STORE_URL =
    "https://apps.apple.com/us/app/budget-app-tapsheet/id6757964945";

// Opens the App Store app directly instead of the web page.
const APP_STORE_APP_URL =
    "itms-apps://apps.apple.com/us/app/budget-app-tapsheet/id6757964945";

// Rendered by download.html, which explains how to leave the in-app browser.
const FALLBACK_URL = "/download.html";

export const isInAppBrowser = (ua: string = navigator.userAgent) =>
    /Instagram|Threads|FBAN|FBAV|FB_IAB|FBIOS/i.test(ua);

export const isIOS = (ua: string = navigator.userAgent) =>
    /iPhone|iPad|iPod/i.test(ua) ||
    // iPadOS reports itself as a Mac.
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// iOS 15.4+ throws a TypeError when assigning some custom schemes, and an
// unsupported scheme is a no-op, so neither case should break the chain.
const tryScheme = (url: string) => {
    try {
        window.location.href = url;
    } catch {
        // Scheme not supported on this device — move on to the next attempt.
    }
};

// Runs `fn` unless we have already been backgrounded, which means an earlier
// attempt succeeded and handed off to another app.
const scheduleAttempt = (fn: () => void, delay: number) => {
    const timer = window.setTimeout(() => {
        if (!document.hidden) {
            fn();
        }
    }, delay);

    window.addEventListener("pagehide", () => window.clearTimeout(timer), {
        once: true,
    });
};

// Send the user to the App Store, escaping the in-app browser if we are in one.
// Call this from a tap handler: custom schemes are far more likely to be
// honoured inside a user gesture than on page load.
export const openAppStore = () => {
    if (!isInAppBrowser() || !isIOS()) {
        window.location.href = APP_STORE_URL;
        return;
    }

    // 1. Ask Instagram to reopen the URL in the system browser, which can then
    //    open the App Store normally.
    tryScheme(
        `instagram://extbrowser?url=${encodeURIComponent(APP_STORE_URL)}`,
    );

    // 2. Still here — ask iOS to open the App Store app itself.
    scheduleAttempt(() => tryScheme(APP_STORE_APP_URL), 400);

    // 3. Still here — show the manual "open in browser" instructions.
    scheduleAttempt(() => {
        window.location.href = FALLBACK_URL;
    }, 1200);
};
